import { useState } from "react";
import { cn } from "@/utils/cn";

interface PrintButtonProps {
  /** Prépare puis lance l'impression. */
  onPrint: () => void | Promise<void>;
  label?: string;
  className?: string;
}

/**
 * Bouton d'impression d'une fiche détail. Prépare le document (même mise en
 * page que le récapitulatif PDF, en-tête de marque et pied de page inclus)
 * puis ouvre la boîte de dialogue d'impression du navigateur.
 */
export default function PrintButton({ onPrint, label = "Imprimer", className }: PrintButtonProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async () => {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await onPrint();
    } catch (err) {
      setError(err instanceof Error ? err.message : "L'impression a échoué.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={cn("relative", className)}>
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        className={cn(
          "inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer sm:w-auto",
          busy && "cursor-wait opacity-80"
        )}
      >
        <i
          className={cn(busy ? "ri-loader-4-line animate-spin" : "ri-printer-line", "text-base text-secondary-700")}
          aria-hidden="true"
        />
        {busy ? "Préparation…" : label}
      </button>

      {error && (
        <p className="absolute right-0 top-full z-20 mt-1.5 w-60 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-xs text-primary-800">
          {error}
        </p>
      )}
    </div>
  );
}